// 📄 pages/Admin/components/EscenarioPruebaFormModal.tsx
import React, { useState } from 'react';
import { X, Save, Plus, Trash2 } from 'lucide-react';
import {
  EscenarioPrueba, EscenarioPruebaData, saveEscenarioPrueba
} from '../../../services/catalogoCasosPruebaService.ts';
import { CampoPrueba } from '../../../services/casoPruebaService.ts';
import { canalesDe } from '../../Proyectos/components/CasoPruebaFormModal.tsx';
import { SOLUCION_OPTIONS } from '../../Proyectos/solucionesConfig.ts';

interface EscenarioPruebaFormModalProps {
  escenario: EscenarioPrueba | null; // null = alta nueva
  onClose: () => void;
  onSaved: () => void;
}

const generarId = (): string => `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;

const EMPTY_FORM: EscenarioPruebaData = { nombre: '', solucion: 'commerce', campos: [], detalles: '' };

export default function EscenarioPruebaFormModal({ escenario, onClose, onSaved }: EscenarioPruebaFormModalProps) {
  const [form, setForm] = useState<EscenarioPruebaData>(
    escenario
      ? { nombre: escenario.nombre, solucion: escenario.solucion, canalId: escenario.canalId, canalNombre: escenario.canalNombre, campos: escenario.campos, detalles: escenario.detalles }
      : EMPTY_FORM
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const opcionesCanal = canalesDe(form.solucion);

  const handleSolucionChange = (solucion: string) => setForm({ ...form, solucion, canalId: undefined, canalNombre: undefined });
  const handleCanalChange = (canalId: string) => {
    const canal = opcionesCanal.find((c) => c.id === canalId);
    setForm({ ...form, canalId: canalId || undefined, canalNombre: canal?.label });
  };

  const handleCampoChange = (index: number, campo: Partial<CampoPrueba>) => {
    const campos = [...form.campos];
    campos[index] = { ...campos[index], ...campo };
    setForm({ ...form, campos });
  };
  const addCampo = () => setForm({ ...form, campos: [...form.campos, { nombre: '', valor: '' }] });
  const removeCampo = (index: number) => setForm({ ...form, campos: form.campos.filter((_, i) => i !== index) });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!form.nombre.trim()) { setError('El nombre del escenario es obligatorio.'); return; }

    setSaving(true);
    try {
      await saveEscenarioPrueba(escenario?.id || generarId(), form, !escenario);
      onSaved();
    } catch (err: any) {
      setError(err.message || 'Error al guardar el escenario.');
    } finally {
      setSaving(false);
    }
  };

  const inputClass = "w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-indigo-500";
  const labelClass = "block text-xs font-medium text-gray-400 mb-1";

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-[rgb(var(--c-surface))] border border-white/10 rounded-2xl w-full max-w-xl shadow-2xl my-8">
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <h3 className="text-lg font-medium text-white">{escenario ? 'Editar escenario sugerido' : 'Nuevo escenario sugerido'}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-xs">{error}</div>}

          <div>
            <label className={labelClass}>Nombre del escenario*</label>
            <input
              type="text" value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })}
              placeholder="ej. Creación de orden de compra" className={inputClass}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Solución*</label>
              <select value={form.solucion} onChange={(e) => handleSolucionChange(e.target.value)} className={inputClass}>
                {SOLUCION_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
            {opcionesCanal.length > 0 && (
              <div>
                <label className={labelClass}>Canal</label>
                <select value={form.canalId || ''} onChange={(e) => handleCanalChange(e.target.value)} className={inputClass}>
                  <option value="">Sin canal específico</option>
                  {opcionesCanal.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                </select>
              </div>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className={labelClass}>Datos de prueba sugeridos (ejemplo)</label>
              <button type="button" onClick={addCampo} className="flex items-center gap-1 text-xs text-indigo-400 hover:text-indigo-300">
                <Plus className="w-3.5 h-3.5" /> Agregar dato
              </button>
            </div>
            {form.campos.length === 0 ? (
              <p className="text-xs text-gray-500">Sin datos de ejemplo todavía — ej. Sociedad, Proveedor, Material.</p>
            ) : (
              <div className="space-y-2">
                {form.campos.map((campo, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input
                      type="text" value={campo.nombre} onChange={(e) => handleCampoChange(i, { nombre: e.target.value })}
                      placeholder="Nombre (ej. Sociedad)" className={`${inputClass} flex-1`}
                    />
                    <input
                      type="text" value={campo.valor} onChange={(e) => handleCampoChange(i, { valor: e.target.value })}
                      placeholder="Valor de ejemplo (ej. 1234)" className={`${inputClass} flex-1`}
                    />
                    <button type="button" onClick={() => removeCampo(i)} className="text-gray-500 hover:text-red-400 flex-shrink-0">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <label className={labelClass}>Detalles sugeridos</label>
            <textarea
              value={form.detalles}
              onChange={(e) => setForm({ ...form, detalles: e.target.value })}
              rows={4}
              placeholder="Pasos del escenario, validaciones a realizar, resultado esperado..."
              className={`${inputClass} resize-none`}
            />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-white hover:bg-white/5 transition-all">Cancelar</button>
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 disabled:bg-gray-600 px-4 py-2 rounded-lg text-sm font-semibold text-[#fff] transition-all"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
